/**
 * SPO流程 RTK Query API
 */

import { createApi } from '@reduxjs/toolkit/query/react';
import { createBaseQuery } from './baseApi';
import type {
  SPOProcess,
  CreateSPORequest,
  GenerateELRequest,
  SignELRequest,
  RecordSPSignatureRequest,
  UploadPaymentProofRequest,
  SubmitIssuerInfoRequest,
  GenerateFrameworkRequest,
  UploadDraftRequest,
  SubmitFeedbackRequest,
  UploadReportRequest,
  RecordEmailRequest,
  SPOListQuery,
  EmailListQuery,
  ApiResponse,
  SPOListResponse,
  EmailListResponse,
} from '../../types/spo';

export const spoApi = createApi({
  reducerPath: 'spoApi',
  baseQuery: createBaseQuery(),
  tagTypes: ['SPOProcess', 'SPOList', 'EmailList'],
  endpoints: builder => ({
    // ==================== 基础CRUD ====================

    // 创建SPO流程
    createSPO: builder.mutation<ApiResponse<SPOProcess>, CreateSPORequest>({
      query: data => ({
        url: '/spo/create',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['SPOList'],
    }),

    // 获取SPO详情
    getSPO: builder.query<ApiResponse<SPOProcess>, string>({
      query: spoId => `/spo/${spoId}`,
      providesTags: (result, error, spoId) => [
        { type: 'SPOProcess', id: spoId },
      ],
    }),

    // 按项目ID获取SPO
    getSPOByProject: builder.query<ApiResponse<SPOProcess>, string>({
      query: projectId => `/spo/project/${projectId}`,
      providesTags: result =>
        result?.data ? [{ type: 'SPOProcess', id: result.data._id }] : [],
    }),

    // 获取SPO列表
    getSPOList: builder.query<
      ApiResponse<SPOListResponse>,
      SPOListQuery | void
    >({
      query: params => {
        if (!params) {
          return '/spo/list';
        }
        return {
          url: '/spo/list',
          params,
        };
      },
      providesTags: ['SPOList'],
    }),

    // 更新SPO
    updateSPO: builder.mutation<
      ApiResponse<SPOProcess>,
      { spoId: string; data: Partial<SPOProcess> }
    >({
      query: ({ spoId, data }) => ({
        url: `/spo/${spoId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { spoId }) => [
        { type: 'SPOProcess', id: spoId },
        'SPOList',
      ],
    }),

    // 删除SPO
    deleteSPO: builder.mutation<ApiResponse, string>({
      query: spoId => ({
        url: `/spo/${spoId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['SPOList'],
    }),

    // ==================== Engagement Letter ====================

    // 生成EL
    generateEL: builder.mutation<
      ApiResponse,
      { spoId: string; data: GenerateELRequest }
    >({
      query: ({ spoId, data }) => ({
        url: `/spo/${spoId}/el/generate`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { spoId }) => [
        { type: 'SPOProcess', id: spoId },
      ],
    }),

    // 签署EL
    signEL: builder.mutation<
      ApiResponse,
      { spoId: string; data: SignELRequest }
    >({
      query: ({ spoId, data }) => ({
        url: `/spo/${spoId}/el/sign`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { spoId }) => [
        { type: 'SPOProcess', id: spoId },
      ],
    }),

    // 发送EL到S&P
    sendELToSP: builder.mutation<ApiResponse, string>({
      query: spoId => ({
        url: `/spo/${spoId}/el/send-to-sp`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, spoId) => [
        { type: 'SPOProcess', id: spoId },
        'EmailList',
      ],
    }),

    // 记录S&P邮件签署
    recordSPSignature: builder.mutation<
      ApiResponse,
      { spoId: string; data: RecordSPSignatureRequest }
    >({
      query: ({ spoId, data }) => ({
        url: `/spo/${spoId}/el/record-sp-signature`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { spoId }) => [
        { type: 'SPOProcess', id: spoId },
        'EmailList',
      ],
    }),

    // 获取EL状态
    getELStatus: builder.query<ApiResponse, string>({
      query: spoId => `/spo/${spoId}/el/status`,
      providesTags: (result, error, spoId) => [
        { type: 'SPOProcess', id: spoId },
      ],
    }),

    // ==================== 付款凭证 ====================

    // 上传付款凭证
    uploadPaymentProof: builder.mutation<
      ApiResponse,
      { spoId: string; data: UploadPaymentProofRequest }
    >({
      query: ({ spoId, data }) => ({
        url: `/spo/${spoId}/payment/upload`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { spoId }) => [
        { type: 'SPOProcess', id: spoId },
      ],
    }),

    // 审核付款
    verifyPayment: builder.mutation<
      ApiResponse,
      { spoId: string; approved: boolean; notes?: string }
    >({
      query: ({ spoId, ...data }) => ({
        url: `/spo/${spoId}/payment/verify`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { spoId }) => [
        { type: 'SPOProcess', id: spoId },
      ],
    }),

    // 获取付款状态
    getPaymentStatus: builder.query<ApiResponse, string>({
      query: spoId => `/spo/${spoId}/payment/status`,
      providesTags: (result, error, spoId) => [
        { type: 'SPOProcess', id: spoId },
      ],
    }),

    // ==================== 发行人提交 ====================

    // 提交项目信息
    submitIssuerInfo: builder.mutation<
      ApiResponse,
      { spoId: string; data: SubmitIssuerInfoRequest }
    >({
      query: ({ spoId, data }) => ({
        url: `/spo/${spoId}/issuer/submit`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { spoId }) => [
        { type: 'SPOProcess', id: spoId },
      ],
    }),

    // 获取提交状态
    getIssuerStatus: builder.query<ApiResponse, string>({
      query: spoId => `/spo/${spoId}/issuer/status`,
      providesTags: (result, error, spoId) => [
        { type: 'SPOProcess', id: spoId },
      ],
    }),

    // ==================== SPO框架 ====================

    // 生成SPO框架
    generateFramework: builder.mutation<
      ApiResponse,
      { spoId: string; data: GenerateFrameworkRequest }
    >({
      query: ({ spoId, data }) => ({
        url: `/spo/${spoId}/framework/generate`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { spoId }) => [
        { type: 'SPOProcess', id: spoId },
      ],
    }),

    // ==================== 草稿/反馈/报告 ====================

    // 上传SPO草稿
    uploadDraft: builder.mutation<
      ApiResponse,
      { spoId: string; data: UploadDraftRequest }
    >({
      query: ({ spoId, data }) => ({
        url: `/spo/${spoId}/draft/upload`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { spoId }) => [
        { type: 'SPOProcess', id: spoId },
        'EmailList',
      ],
    }),

    // 提交反馈
    submitFeedback: builder.mutation<
      ApiResponse,
      { spoId: string; data: SubmitFeedbackRequest }
    >({
      query: ({ spoId, data }) => ({
        url: `/spo/${spoId}/feedback/submit`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { spoId }) => [
        { type: 'SPOProcess', id: spoId },
      ],
    }),

    // 上传最终报告
    uploadReport: builder.mutation<
      ApiResponse,
      { spoId: string; data: UploadReportRequest }
    >({
      query: ({ spoId, data }) => ({
        url: `/spo/${spoId}/report/upload`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { spoId }) => [
        { type: 'SPOProcess', id: spoId },
        'SPOList',
        'EmailList',
      ],
    }),

    // ==================== 邮件通信 ====================

    // 记录邮件
    recordEmail: builder.mutation<
      ApiResponse,
      { spoId: string; data: RecordEmailRequest }
    >({
      query: ({ spoId, data }) => ({
        url: `/spo/${spoId}/email/record`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { spoId }) => [
        { type: 'SPOProcess', id: spoId },
        'EmailList',
      ],
    }),

    // 获取邮件列表
    getEmailList: builder.query<
      ApiResponse<EmailListResponse>,
      { spoId: string; params?: EmailListQuery }
    >({
      query: ({ spoId, params }) => ({
        url: `/spo/${spoId}/email/list`,
        params,
      }),
      providesTags: ['EmailList'],
    }),
  }),
});

export const {
  // 基础CRUD
  useCreateSPOMutation,
  useGetSPOQuery,
  useGetSPOByProjectQuery,
  useGetSPOListQuery,
  useUpdateSPOMutation,
  useDeleteSPOMutation,

  // Engagement Letter
  useGenerateELMutation,
  useSignELMutation,
  useSendELToSPMutation,
  useRecordSPSignatureMutation,
  useGetELStatusQuery,

  // 付款凭证
  useUploadPaymentProofMutation,
  useVerifyPaymentMutation,
  useGetPaymentStatusQuery,

  // 发行人提交
  useSubmitIssuerInfoMutation,
  useGetIssuerStatusQuery,

  // SPO框架
  useGenerateFrameworkMutation,

  // 草稿/反馈/报告
  useUploadDraftMutation,
  useSubmitFeedbackMutation,
  useUploadReportMutation,

  // 邮件通信
  useRecordEmailMutation,
  useGetEmailListQuery,
} = spoApi;
